import { useEffect, useState } from "react";
import ApiService from "../core/service/ApiService";
import { Button, Card, Divider, List, Table, Tabs, Typography } from "antd";
import {
    CloudDownloadOutlined,
    FileImageOutlined,
    FilePdfOutlined, FolderOpenOutlined,
    LoadingOutlined,
    ShareAltOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import { LocalStorageService } from "../core/service/LocalStorageService";
import PatientLocalStorage from "./PatientLocalStorage";
import { isMobile } from "react-device-detect";
import TokenDurationModal from "./TokenDurationModal";

export default function PatientResult() {
    const [reports, setReports] = useState([]);
    const [studies, setStudies] = useState([]);
    const patient = PatientLocalStorage.getInformation()

    // Load reports + studies. Each GetStudies row is already one study; reports are attached to their study by
    // studyId (NOT by studyDate — two different studies can share a date).
    const load = async () => {
        const reportsRes = await ApiService.PatientApp.GetReports();
        const reportData = (reportsRes && reportsRes.success) ? reportsRes.data : [];
        setReports(reportData);

        const studiesRes = await ApiService.PatientApp.GetStudies();
        if (studiesRes && studiesRes.success) {
            const byStudy = {};
            for (const r of reportData) {
                if (!r.studyId) continue;
                if (!byStudy[r.studyId]) byStudy[r.studyId] = [];
                byStudy[r.studyId].push(r);
            }
            setStudies((studiesRes.data || []).map(s => ({ ...s, reports: byStudy[s.studyId] || [] })));
        }
    };


    // Share via the Web Share API when available, otherwise copy to clipboard.
    const shareOrCopy = async (title, text, url) => {
        if (navigator.share) {
            try { await navigator.share({ title, text, url }); }
            catch (e) { navigator.clipboard.writeText(url).then(() => alert(t('Link copied to clipboard'))); }
        } else {
            navigator.clipboard.writeText(url).then(() => alert(t('Link copied to clipboard')));
        }
    }

    // RIS report rows carry studyId: mint a study access key, then open the matching report PDF straight from RIS.
    const openRisPdf = async (item) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, 2, 'report');
        if (res.success) {
            const report = (res.data?.reports || []).find(r => r.reportId === item.reportId);
            if (report?.pdfUrl) window.open(report.pdfUrl, '_blank');
            else alert(t('Report not available'));
        }
    }

    // Local PATIENT_RESULT appointment files (still served by HIS).
    const openPdf = async (fileId) => {
        const res = await ApiService.PatientApp.DownloadFile(fileId)
        if (res.success) {
            window.open(res.data, "_blank");
        }
    }

    // Download is proxied through HIS: the browser hits a HIS endpoint that streams the zip (DICOM + embedded
    // viewer) straight from RIS — same artifact as a "Share Study / Include Viewer" link, but never exposing RIS.
    const downloadZip = (item) => {
        const a = document.createElement('a');
        a.href = ApiService.PatientApp.GetStudyZipUrl(item.studyId);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    const openStudy = async (studyId) => {
        const res = await ApiService.PatientApp.GetStudyAccess(studyId, 2, 'viewer');
        if (res.success && res.data?.viewerUrl) window.open(res.data.viewerUrl);
    }

    // Share the online dwv viewer link (served by RIS).
    const getOhifLink = async (item, tokenType) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, tokenType);
        if (res.success && res.data?.viewerUrl) {
            await shareOrCopy(t('Study Viewer Link'), `${patient?.name} ${patient?.lastname}\n${item.studyDescription || ''}`, res.data.viewerUrl);
        }
    }

    // Share the DICOM zip download link (served by RIS).
    const getLink = async (item, tokenType) => {
        const res = await ApiService.PatientApp.GetStudyAccess(item.studyId, tokenType);
        if (res.success && res.data?.zipUrl) {
            await shareOrCopy(t('Study Link'), `${patient?.name} ${patient?.lastname}\n${item.studyDescription || ''}`, res.data.zipUrl);
        }
    }

    const getProfileLink = async (tokenType) => {
        const res = await ApiService.PatientApp.GetAccessToken(tokenType);
        if (res.success) {
            // The profile link carries a JWT, so it's long; shorten it via the URL-shortener endpoint.
            const longUrl = `${window?.AppSettings?.server?.api}/ProfileShare?access_token=${res.data}`;
            const shortRes = await ApiService.PatientApp.CreateUrl(longUrl);
            const url = (shortRes && shortRes.success)
                ? `${window?.AppSettings?.server?.api}/api/PatientApp/GetUrl/${shortRes.data}`
                : longUrl;
            await shareOrCopy(t('Study Viewer Link'), `${patient?.name} ${patient?.lastname}`, url);
        }
    }

    useEffect(() => {
        load();
    }, []);

    const renderReportsList = (dataSource) => (
        <List
            itemLayout="horizontal"
            dataSource={dataSource}
            size="large"
            renderItem={(item, index) => (
                <List.Item>
                    <List.Item.Meta
                        title={(
                            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column' }}>
                                <Typography.Text style={{ fontSize: '13pt', display: 'flex', alignItems: 'start' }}>
                                    <div>
                                        <div style={{ marginTop: 8, marginLeft: 0 }}>
                                            {
                                                (item.confirmDate) && (
                                                    <Typography.Link style={{ fontSize: '12pt' }}
                                                        onClick={() => {
                                                            if (item.reportId && item.reportId !== 0) {
                                                                openRisPdf(item);
                                                            } else {
                                                                openPdf(item.fileId);
                                                            }
                                                        }}
                                                    >
                                                        <FilePdfOutlined style={{ fontSize: '15px', color: 'grey', marginRight: 4 }} />
                                                        {new Date(item.confirmDate).toLocaleDateString()}
                                                        &emsp;
                                                        {item.displayName}
                                                    </Typography.Link>
                                                )
                                            }
                                            {
                                                !(item.confirmDate) && (
                                                    <Typography.Text style={{ fontSize: '12pt' }}>
                                                        <LoadingOutlined style={{ fontSize: '18px' }} /> {t('Waiting...')}
                                                        &emsp;
                                                        {item.displayName}
                                                    </Typography.Text>
                                                )
                                            }
                                        </div>
                                        <div style={{ display: 'flex', color: '#777777', flexDirection: 'row', flexWrap: 'wrap' }}>
                                            <div style={{ fontSize: '10pt', width: 120, marginLeft: 0 }}>{t('Accession Number')}:</div>
                                            <div style={{ fontSize: '10pt' }}>{item.accessionNumber ? item.accessionNumber : '-'}</div>
                                        </div>
                                    </div>
                                </Typography.Text>
                            </div>
                        )}
                    />
                </List.Item>
            )}
        />
    );

    const renderAllStudyList = (dataSource) => (
        <List
            itemLayout="horizontal"
            dataSource={dataSource}
            size="large"
            renderItem={(item, index) => (
                <List.Item>
                    <List.Item.Meta
                        title={(
                            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column' }}>
                                <Typography.Text style={{ fontSize: '13pt', display: 'flex', alignItems: 'start' }}>
                                    <div>
                                        {new Date(item.studyDate).toLocaleDateString()} - {item.studyDescription} ({item.modality})
                                        <div style={{ display: 'flex', color: '#777777', flexDirection: 'row', flexWrap: 'wrap' }}>
                                            <div style={{ fontSize: '10pt', width: 100, marginLeft: 0 }}>{t('Image Count')}:</div>
                                            <div style={{ fontSize: '10pt' }}>{item.numberOfStudyRelatedInstance}</div>
                                            
                                            {
                                                item.reports?.map(r => (
                                                    <div style={{ fontSize: '10pt', width: 100, marginLeft: 16 }}>{r.accessionNumber}</div>
                                                ))
                                            }
                                        </div>
                                        <br />
                                        <div style={{ display: 'flex', color: '#777777', flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
                                                <Button
                                                    type="primary"
                                                    style={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={() => openStudy(item.studyId)}
                                                    icon={<FolderOpenOutlined />}
                                                >
                                                    Görüntüleyicide Aç
                                                </Button>
                                                <Button
                                                    color={'success'}
                                                    style={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={() => downloadZip(item)}
                                                    icon={<CloudDownloadOutlined />}
                                                >
                                                    DICOM İndir
                                                </Button>
                                                <Button
                                                    type="dashed"
                                                    style={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={async () => {
                                                        const tokenType = await TokenDurationModal(1);
                                                        if (tokenType !== null) {
                                                            await getLink(item, tokenType);
                                                        }
                                                    }}
                                                    icon={<ShareAltOutlined />}
                                                >
                                                    DICOM Paylaş
                                                </Button>
                                                <Button
                                                    type="dashed"
                                                    style={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={async () => {
                                                        const tokenType = await TokenDurationModal(1);
                                                        if (tokenType !== null) {
                                                            await getOhifLink(item, tokenType);
                                                        }
                                                    }}
                                                    icon={<ShareAltOutlined />}
                                                >
                                                    Görüntüleyici Linki Paylaş
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Typography.Text>
                            </div>
                        )}
                    />
                </List.Item>
            )}
        />
    );

    return (
        <div>
            <Card>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'start', gap: 24, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 400 }}>
                        <table>
                            {<tr>
                                <td colSpan={2}>
                                    <Typography.Title level={3}>{patient?.name} {patient?.lastname}</Typography.Title>
                                </td>
                            </tr>}
                            <tr>
                                <td>{t('Identity')}:</td>
                                <td>{patient?.customId}</td>
                            </tr>
                            <tr>
                                <td>{t('Birthdate')}:</td>
                                <td>{patient?.birthDay?.toString()?.padStart(2, '0')}/{patient?.birthMonth?.toString()?.padStart(2, '0')}/{patient?.birthYear}</td>
                            </tr>
                            <tr>
                                <td>{t('Gender')}:</td>
                                <td>{t(patient?.gender)}</td>
                            </tr>
                        </table>
                    </div>
                </div>
                <Typography.Text>
                    <br />
                    Sistemde kayıtlı rapor ve çekimlerinizi aşağıda listeleyebilirsiniz. Eksik veya hatalı bir bilgi varsa lütfen <b><i>"{t('Contact')}"</i></b> kısmından bize ulaşın.
                    <br />
                </Typography.Text>
                <br />
                <Button
                    style={{ flex: 1, width: 'auto', display: 'flex', alignItems: 'center' }}
                    onClick={async () => {
                        const tokenType = await TokenDurationModal(1);
                        if (tokenType !== null) {
                            await getProfileLink(tokenType);
                        }
                    }}
                >
                    <ShareAltOutlined />
                    Profili Paylaş
                </Button>



            </Card>
            <div style={{ backgroundColor: 'white', marginTop: 25, padding: 10 }}>
                <Tabs defaultActiveKey="2" items={[
                    {
                        key: '2',
                        label: t('Studies'),
                        children: renderAllStudyList(studies)
                    },
                    {
                        key: '1',
                        label: 'Raporlar',
                        children: renderReportsList(reports)
                    }
                ]} />
            </div >
        </div >
    )
}